import React from "react";
import { sum } from 'd3';
import { normalized } from '../../selectors/normalized-weights';
import logoStore from './logo-store';

import PortfoliosMatchModal from '../../Report/PortfoliosMatchModal'

import stressTest from './stress-test'
import monteCarlo from './monte-carlo'
import stressTestData from './stress-test-data'
import getCompliance from './compliance'

const runAnalysis = () => (dispatch, getState) => {
  const state = getState();
  const normalizedWeights = normalized(state);

  dispatch({ type: 'LOADING' });
  dispatch(getCompliance()); // call again now that user has selected entityCCY
  dispatch(stressTest('GET')); // call every run because non-permanents are discarded on portfolio edit
  dispatch(monteCarlo('GET')); // same
  if(state.customStressTests.length === 2) dispatch(stressTestData()); // call on initial run, not subsequent reruns

  const body = (state.entry.compareMode ? ["original","proposed"] : ["original"]).map(side => ({
    map: !state.entry.compareMode,
    clientEmail: state.clientEmail,
    preparedForEmail: state.entry.original.preparedForEmail,
    sedol: null,
    isin: null,
    cusip: null,
    name: state.entry[side].name,
    totalValue: sum(
      state.entry[side].portfolio.map(d => state.entry[side].weightings[d.ticker])
    ),
    investmentGoal: state.entry.compareMode ? "C" : state.entry[side].investmentGoal,
    bestModel: state.entry[side].bestModel,
    isDeleted: false,
    holdings: [
      {
        tickers: state.entry[side].portfolio.map(d => d.ticker),
        weights: state.entry[side].portfolio.map(d => normalizedWeights[side][d.ticker]),
      },
    ],
    saveToSF: process.env.NODE_ENV === 'production',
    filterFundYears: state.entry[side].filterFundYears,
    filterFundSize: state.entry[side].filterFundSize,
    wtRegion: state.wtRegion,
    entityCCY: state.entry[side].entityCCY,
    benchmarks: state.entry.benchmarks
      .filter(d => d.externalID === state.entry.selectedBenchmark),
    modelProposalTicker: state.entry[side].modelProposalTicker,
    personalStrategyId: state.entry[side].portfolio.personalStrategyId,
  }))

  const url = process.env.REACT_APP_OFFLINE_MODE
    ? 'canned-responses/run-analysis.json'
    : `${process.env.REACT_APP_API_URL}/portfolio/`;
  fetch(url, {
    method: process.env.REACT_APP_OFFLINE_MODE ? 'GET' : 'POST',
    body: process.env.REACT_APP_OFFLINE_MODE ? null : JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: state.token && `Token ${state.token}`,
    },
  })
    .then(d => {
      if (d.status === 403) {
        dispatch({ type: 'DIALOG', message: state.complianceText.analysisUnavailable })
      } else if (d.status === 422) {
        return d.json().then(({ detail }) => {
          dispatch({ type: 'DIALOG', message: detail });
        });
      } else if (d.status === 500){
        dispatch({ type: 'DIALOG', message: state.complianceText.dataCurrentlyUnavailable })
      }
      return d.json().then(data => {
        if(data.length === 0){
          dispatch({ type: 'DIALOG', message: state.complianceText.dataCurrentlyUnavailable })
          throw new Error('Data currently unavailable')
        }
        if(state.dataCallback) {
          state.dataCallback('portfolio', data);
        }
        fetch(`${process.env.REACT_APP_API_URL}/portfolio/save_to_salesforce/?id=${data[0].id}`, {
          method: 'POST',
          body: JSON.stringify({
            name: data[0].name
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: state.token && `Token ${state.token}`,
          }
        })
        .then(d => {
          if(d.status < 400){
            dispatch({ type: 'SET_SALESFORCE_COMPLETE', status: true })
            
            const originalPortfolioLabel = (state.entry.original.name) ? state.entry.original.name : state.complianceText.originalPortfolioLabel;
            const proposedPortfolioLabel = (state.entry.proposed.name) ? state.entry.proposed.name : state.complianceText.proposedPortfolioLabel;
  
            // Set up the report name
            let reportNameLabel = '';
            if (state.reportName) {
              reportNameLabel = state.reportName.toUpperCase();
            } else if (originalPortfolioLabel !== state.complianceText.originalPortfolioLabel) {
              reportNameLabel = (originalPortfolioLabel + (proposedPortfolioLabel !== state.complianceText.proposedPortfolioLabel ? 
                ' vs ' + proposedPortfolioLabel : '')).toUpperCase();
            } else if (proposedPortfolioLabel !== state.complianceText.proposedPortfolioLabel) {
              reportNameLabel = (state.complianceText.unnamedPortfolioLabel + ' vs ' + proposedPortfolioLabel).toUpperCase();
            } else {
              reportNameLabel = state.complianceText.unnamedPortfolioLabel.toUpperCase();
            }
            dispatch({ type: 'SET_REPORT_NAME', name:  reportNameLabel})
            
          } else {
            dispatch({ type: "SET_PDF_STATUS", status: "unavailable" })
            dispatch({ type: "SET_TICKET_STATUS", status: "unavailable" })
          }
        })
        .catch(error => console.error(error))

        try{
          dispatch({ type: 'SET_ANALYSIS', analysis: data });
        } catch (error){
          console.log(error);
        }
        if (state.clientPdf.allowClientLogo) dispatch(logoStore('GET'));
        if (state.entry.original.bestModel || state.entry.compareMode) dispatch({ type: "SET_COMPOSITION_SPLIT", split: true })
        if (state.awaitingNewData) {
          // window.location.hash = "";
          dispatch({ type: "SET_AWAIT_STATUS", status: false })
          dispatch({ type: 'SET_PDF_STATUS', status: "unsent" })
          dispatch({ type: 'SET_TICKET_STATUS', status: "unsent" })
          dispatch({ type: 'SET_GRAPHIC_LAYOUT', layout: "Bar chart" })
        }

        // Show the Modal is the holdings are different and this is not a comparison run
        if (data[0].holdings[0].tickers.length === data[1].holdings[0].tickers.length &&
          data[0].holdings[0].tickers.reduce((t,v,i) => t && v === data[1].holdings[0].tickers[i], true) &&
          data[0].holdings[0].weights.reduce((t,v,i) => t && v === data[1].holdings[0].weights[i], true) &&
          !state.entry.compareMode) {
          dispatch({ type: 'DIALOG', message: <PortfoliosMatchModal />, buttons: [] })
        }
      });
    })
    .catch(error => console.error(error));
};

export default runAnalysis;



// WEBPACK FOOTER //
// ./src/actions/query-api/run-analysis.js